// Templates
import { TemplateLoggedInComponent } from "#components/template-logged-in/template-logged-in.component";
import { TemplateGuestComponent } from "#components/template-guest/template-guest.component";

// Guards
import { PermissionGuard } from "#guards/permission/permission.guard";
import { AuthGuard } from "#guards/auth/auth.guard";
import { AUTH_PATH } from "#src/app/shared/variables/routes";

// Models
import { AppRoute, AppRoutes, RegistationPrivateRouteData, RouteData } from "#models/router";
import { Permission } from "#models/permissions";

const getPrivateRoute = (
    path: string | undefined,
    loadChildren: Promise<RouteData>,
    can?: Permission
): AppRoute => {
    return {
        path,
        loadChildren: () => loadChildren.then((m) => m.ROUTES),
        ...(can
            ? {
                  canActivate: [PermissionGuard],
                  data: { can },
              }
            : {}),
    };
};

const getPrivateRoutes = (routes: RegistationPrivateRouteData[]): AppRoutes => {
    return routes.map((route) => getPrivateRoute(...route));
};

const privateRoutes: AppRoutes = getPrivateRoutes([
    // Solo
    ["", import("#pages/home/home.routes")],
    ["profile", import("#pages/profile/profile.routes")],

    ["roles", import("#pages/roles/roles.routes"), "roleView"],
    ["permissions", import("#pages/permissions/permissions.routes"), "permissionView"],
    ["config", import("#pages/config/config.routes"), "configView"],
    ["jobs", import("#pages/jobs/jobs.routes"), "jobView"],
    ["failed-jobs", import("#pages/failed-jobs/failed-jobs.routes"), "failedJobView"],
    ["system-logs", import("#pages/system-logs/system-logs.routes"), "systemLogView"],

    [
        "cli-commands",
        import("#src/app/pages/cli-commands/cli-commands.routes"),
        "artisanCommandView",
    ],

    //Product reviews
    [
        "product-reviews",
        import("#pages/product-reviews/product-reviews.routes"),
        "productReviewView",
    ],
    [
        "product-reviews/:id",
        import("#pages/product-review/product-review.routes"),
        "productReviewView",
    ],

    // Feedbacks
    ["feedbacks", import("#pages/feedbacks/feedbacks.routes"), "feedbackView"],

    // Seo Routing
    ["seo-routing", import("#pages/seo-routing/seo-routing.routes"), "seoLinkView"],

    // Portal Translations
    [
        "portal-translations",
        import("#pages/portal-translations/portal-translations.routes"),
        "translationView",
    ],

    // SEO Redirects
    [
        "seo-redirects",
        import("#pages/seo-redirects/seo-redirects-routing.routes"),
        "seoRedirectView",
    ],

    // Megogo
    ["megogo/subscriptions", import("#pages/megogo/megogo.routes"), "megogoSubscriptionView"],
    [
        "megogo/products",
        import("#pages/megogo-products/megogo.products.routes"),
        "megogoServicesView",
    ],

    // Credit Programs
    [
        "credit-programs/edit/:id",
        import("#pages/edit-credit-program/edit-credit-program.routes"),
        "creditProgramEdit",
    ],
    [
        "credit-programs",
        import("#pages/credit-programs/credit-programs.routes"),
        "creditProgramView",
    ],
    [
        "credit-programs/:id",
        import("#pages/credit-program/credit-program.routes"),
        "creditProgramView",
    ],

    // Static Pages
    ["static-pages", import("#pages/static-pages/static-pages.routes"), "staticPageView"],
    [
        "static-pages/create",
        import("#pages/static-pages-create/static-pages-create.routes"),
        "staticPageCreate",
    ],
    ["static-pages/:id", import("#pages/static-page/static-page.routes"), "staticPageEdit"],
    [
        "static-pages/edit/:id",
        import("#pages/static-page-edit/static-page-edit.routes"),
        "staticPageEdit",
    ],

    // Pages (settings)
    ["pages/:id", import("#pages/pages/pages.routes"), "pageEdit"],

    // Delivery
    ["delivery-methods", import("#pages/delivery-methods/delivery-methods.routes"), "deliveryView"],
    [
        "delivery-methods/edit/:id",
        import("#pages/delivery-method-edit/delivery-method-edit.routes"),
        "deliveryEdit",
    ],
    [
        "delivery-methods/create",
        import("#pages/delivery-method-create/delivery-method-create.routes"),
        "deliveryAdd",
    ],
    [
        "delivery-methods/:id",
        import("#pages/delivery-method/delivery-method.routes"),
        "deliveryView",
    ],

    // Menu Manager
    ["menu-manager", import("#pages/menu-manager/menu-manager.routes"), "menuView"],
    [
        "menu-manager/edit/:id",
        import("#pages/menu-manager-edit/menu-manager-edit.routes"),
        "menuEdit",
    ],

    //Cities & Regions
    ["regions", import("#pages/regions/regions.routes"), "regionView"],
    ["cities", import("#pages/cities/cities.routes"), "cityView"],

    // Users
    ["users", import("#pages/users/users.routes"), "userView"],
    ["users/create", import("#pages/create-user/create-user.routes"), "userAdd"],
    ["users/edit/:id", import("#pages/edit-user/edit-user.routes"), "userEdit"],
    ["users/:id", import("#pages/user/user.routes"), "userView"],

    // FAQ
    ["faq", import("#pages/faqs/faqs.routes"), "faqView"],
    ["faq/:id", import("#pages/faq/faq.routes"), "faqView"],

    // Properties
    [
        "property-groups",
        import("#pages/property-groups/property-groups.routes"),
        "propertyGroupView",
    ],
    [
        "property-groups/:id",
        import("#pages/property-group/property-group.routes"),
        "propertyGroupView",
    ],
    ["properties", import("#pages/properties/properties.routes"), "propertyView"],
    ["properties/:id", import("#pages/property/property.routes"), "propertyView"],
    [
        "property-value-units",
        import("#pages/property-value-units/property-value-units.routes"),
        "propertyValueUnitView",
    ],

    // Categories
    ["categories", import("#pages/categories/categories.routes"), "categoryView"],
    ["categories/create", import("#pages/create-category/create-category.routes"), "categoryAdd"],
    ["categories/edit/:id", import("#pages/edit-category/edit-category.routes"), "categoryEdit"],
    ["categories/:id", import("#pages/category/category.routes"), "categoryView"],

    // Payments
    [
        "payment-methods",
        import("#pages/payment-methods/payment-methods.routes"),
        "paymentMethodView",
    ],
    [
        "payment-methods/create",
        import("#pages/create-payment-method/create-payment-method.routes"),
        "paymentMethodAdd",
    ],
    [
        "payment-methods/edit/:id",
        import("#pages/edit-payment-method/edit-payment-method.routes"),
        "paymentMethodEdit",
    ],
    [
        "payment-methods/:id",
        import("#pages/payment-method/payment-method.routes"),
        "paymentMethodView",
    ],

    // Brands
    ["brands", import("#pages/brands/brands.routes"), "brandView"],
    ["brands/create", import("#pages/create-brand/create-brand.routes"), "brandAdd"],
    ["brands/edit/:id", import("#pages/edit-brand/edit-brand.routes"), "brandEdit"],
    ["brands/:id", import("#pages/brand/brand.routes"), "brandView"],

    // Products
    ["products", import("#pages/products/products.routes"), "productView"],
    ["products/edit/:id", import("#pages/edit-product/edit-product.routes"), "productEdit"],
    ["products/:id", import("#pages/product/product.routes"), "productView"],

    // Product Statuses
    [
        "product-status",
        import("#pages/products-status/products-status.routes"),
        "productViewStatus",
    ],

    // Product Groups
    ["product-groups", import("#pages/product-groups/product-groups.routes"), "productView"],
    ["product-groups/:id", import("#pages/product-group/product-group.routes"), "productView"],

    // Clients
    ["clients", import("#pages/clients/clients.routes"), "clientView"],
    ["clients/create", import("#pages/create-client/create-client.routes"), "clientAdd"],
    ["clients/edit/:id", import("#pages/edit-client/edit-client.routes"), "clientEdit"],
    ["clients/:id", import("#pages/client/client.routes"), "clientView"],

    // Banners
    ["banners", import("#pages/banners/banners.routes"), "bannerView"],
    ["banners/:id", import("#pages/banner/banner.routes"), "bannerView"],

    // Orders
    ["orders", import("#pages/orders/orders.routes"), "orderView"],
    ["orders/:id", import("#pages/order/order.routes"), "orderView"],

    // Events
    ["events", import("#pages/events/events.routes"), "eventView"],

    // Errors
    ["403", import("#pages/error-403/error-403.routes")],

    // Base
    ["**", import("#pages/error-404/error-404.routes")],
]);

const publicRoutes: AppRoutes = [
    {
        path: AUTH_PATH,
        canActivate: [AuthGuard],
        component: TemplateGuestComponent,
        loadChildren: () => import("#pages/auth/auth.routes").then((m) => m.ROUTES),
    },
    {
        path: "404",
        component: TemplateGuestComponent,
        loadChildren: () => import("#pages/error-404/error-404.routes").then((m) => m.ROUTES),
    },
];

export const routes: AppRoutes = [
    ...publicRoutes,
    {
        path: "",
        canActivate: [AuthGuard],
        component: TemplateLoggedInComponent,
        children: privateRoutes,
    },
    {
        path: "**",
        component: TemplateGuestComponent,
        loadChildren: () => import("#pages/error-404/error-404.routes").then((m) => m.ROUTES),
    },
];
